import {
  ICON_CLASS_PLAY,
  ICON_CLASS_PAUSE,
  PLAY_PAUSE_BTN_ID,
  TRACK_TITLE_SPAN_ID,
} from "./constants";
import type { AppConfig } from "./interfaces";
import type { AudioManager } from "./AudioManager";

export class UIManager {
  private audioManager: AudioManager;
  private config: AppConfig;
  private playPauseBtn: HTMLElement;
  private iconElement: HTMLElement | null = null;
  private trackTitleElement: HTMLElement | null = null;

  constructor(audioManager: AudioManager, config: AppConfig) {
    this.audioManager = audioManager;
    this.config = config;
    const btn = document.getElementById(PLAY_PAUSE_BTN_ID);
    if (!btn) {
      throw new Error("Play/Pause button not found!");
    }
    this.playPauseBtn = btn;
    this.iconElement = this.playPauseBtn.querySelector("i");

    this.trackTitleElement = document.getElementById(TRACK_TITLE_SPAN_ID);
    this.setTrackTitle();

    this.setupEventListeners();
    this.updateIcon();
  }

  private setTrackTitle(): void {
    let titleToSet = "Unknown Track";

    if (this.config.trackTitle) {
      titleToSet = `${this.config.trackTitle}`;
    } else if (this.config.track) {
      // Fallback to derive from track path
      const trackName = this.config.track
        .split("/")
        .pop()
        ?.replace(/\.(mp3|wav|ogg|aac)$/i, "");
      if (trackName) {
        titleToSet = trackName.replace(/_/g, " ");
      }
    }

    if (this.trackTitleElement) {
      this.trackTitleElement.textContent = titleToSet;
    } else {
      console.warn("Track title element not found in UIManager.");
    }
    document.title = `${this.config.trackArtist} - ${this.config.trackTitle}`;
  }

  private setupEventListeners(): void {
    this.playPauseBtn.addEventListener("click", (event) => {
      event.stopPropagation();
      this.togglePlayState();
    });

    window.addEventListener("click", (event) => {
      if (this.playPauseBtn.contains(event.target as Node)) {
        return;
      }
      this.togglePlayState();
    });
  }

  public togglePlayState(): void {
    if (this.audioManager.isPlaying) {
      this.audioManager.pause();
    } else {
      this.audioManager.play();
    }
    this.updateIcon();
  }

  private updateIcon(): void {
    if (this.iconElement) {
      if (this.audioManager.isPlaying) {
        this.iconElement.classList.remove(ICON_CLASS_PLAY);
        this.iconElement.classList.add(ICON_CLASS_PAUSE);
      } else {
        this.iconElement.classList.remove(ICON_CLASS_PAUSE);
        this.iconElement.classList.add(ICON_CLASS_PLAY);
      }
    } else {
      console.warn("Play/Pause icon element not found during UI update.");
    }
  }

  public onAudioReady(): void {
    this.updateIcon();
  }
}
